import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../index';
import { FetchState } from '../fetchState';
import { storageResetAction } from '../storeReset';
import { DashboardTileDto } from '@api/models/dashboard/DashboardTileDto';
import { DashboardPage } from '@api/models/enums/DashboardPage';
import { TileScope } from '@api/models/dashboard/TileScope';
import { UserRole } from '@api/models/enums/UserRole';
import { dashboardTileService } from '@api/services/services';
import { selectIdentityCompanyId, selectIdentityRoles } from './identity';
import { hasRole } from '../../utils/roles';

interface RequestTracked {
  requestId: number;
}

export interface DashboardTilesState extends RequestTracked {
  state: FetchState;
  tiles: DashboardTileDto[];
  activePage: DashboardPage | null;
  activeScope: TileScope;
}

const initialState: DashboardTilesState = {
  state: FetchState.None,
  tiles: [],
  activePage: null,
  activeScope: 'personal',
  requestId: 0,
};

export const dashboardTilesSlice = createSlice({
  name: 'dashboardTiles',
  initialState,
  reducers: {
    request(state) {
      state.state = FetchState.Loading;
      state.requestId += 1;
    },
    silentRequest(state) {
      state.requestId += 1;
    },
    success(state, action: PayloadAction<{ requestId: number; tiles: DashboardTileDto[] }>) {
      if (action.payload.requestId !== state.requestId) return;
      state.state = FetchState.Loaded;
      state.tiles = action.payload.tiles;
    },
    error(state, action: PayloadAction<{ requestId: number }>) {
      if (action.payload.requestId !== state.requestId) return;
      state.state = FetchState.Error;
    },
    setTiles(state, action: PayloadAction<DashboardTileDto[]>) {
      state.tiles = action.payload;
    },
    setActivePage(state, action: PayloadAction<DashboardPage>) {
      state.activePage = action.payload;
    },
    setActiveScope(state, action: PayloadAction<TileScope>) {
      state.activeScope = action.payload;
    },
    reset() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(storageResetAction, () => initialState);
  },
});

const actions = dashboardTilesSlice.actions;

export const { setActiveScope } = actions;

/* ---------- Selectors ---------- */

export const selectDashboardTiles = (state: RootState): DashboardTileDto[] => state.dashboardTiles.tiles;

export const selectDashboardTilesState = (state: RootState): FetchState => state.dashboardTiles.state;

export const selectActiveTileScope = (state: RootState): TileScope => state.dashboardTiles.activeScope;

/* ---------- Thunks ---------- */

const resolveScope = (scope: TileScope, getState: () => RootState): TileScope => {
  if (scope === 'personal') return scope;
  const roles = selectIdentityRoles(getState());
  return hasRole(roles, [UserRole.AdminQcs]) ? scope : 'personal';
};

const fetchTilesForScope = async (scope: TileScope, page: DashboardPage, getState: () => RootState): Promise<DashboardTileDto[]> => {
  switch (scope) {
    case 'company': {
      const companyId = selectIdentityCompanyId(getState());
      // AdminQcs admin hasn't picked a company yet — show empty state, not an error.
      if (!companyId) return [];
      const { data } = await dashboardTileService.listCompanyDefaults(companyId, page);
      return data;
    }
    case 'global': {
      const { data } = await dashboardTileService.listGlobalDefaults(page);
      return data;
    }
    case 'personal':
    default: {
      const { data } = await dashboardTileService.listTiles(page);
      return data;
    }
  }
};

export const fetchDashboardTiles = (page: DashboardPage, scope?: TileScope) => async (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(actions.setActivePage(page));
  const requestedScope = scope ?? getState().dashboardTiles.activeScope;
  const effectiveScope = resolveScope(requestedScope, getState);
  dispatch(actions.setActiveScope(effectiveScope));
  dispatch(actions.request());
  const { requestId } = getState().dashboardTiles;
  try {
    const tiles = await fetchTilesForScope(effectiveScope, page, getState);
    dispatch(actions.success({ requestId, tiles }));
  } catch {
    dispatch(actions.error({ requestId }));
  }
};

export const refreshDashboardTiles =
  (silent = false) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { activePage: page, activeScope } = getState().dashboardTiles;
    if (page) {
      const effectiveScope = resolveScope(activeScope, getState);
      if (effectiveScope !== activeScope) dispatch(actions.setActiveScope(effectiveScope));
      dispatch(silent ? actions.silentRequest() : actions.request());
      const { requestId } = getState().dashboardTiles;
      try {
        const tiles = await fetchTilesForScope(effectiveScope, page, getState);
        dispatch(actions.success({ requestId, tiles }));
      } catch {
        dispatch(actions.error({ requestId }));
      }
    }
  };

export const reorderDashboardTiles = (orderedIds: string[]) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const previous = getState().dashboardTiles.tiles;
  const reordered = orderedIds
    .map((id) => previous.find((t) => t.id === id))
    .filter((t): t is DashboardTileDto => !!t)
    .map((t, idx) => ({ ...t, displayOrder: idx }));

  dispatch(actions.setTiles(reordered));
  try {
    await dashboardTileService.reorderTiles(orderedIds);
  } catch {
    await dispatch(refreshDashboardTiles(true));
  }
};
